import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { refresh } from '@/api';
import { useModalStore } from '@/store';
import { useErrorStore } from '@/store/useErrorStore.ts';

import App from './App.tsx';

const setIsOnline = useErrorStore.getState().setIsOnline;
const { closeAllModals, setIsErrorOpen } = useModalStore.getState();

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 5,
        },
    },
    queryCache: new QueryCache({
        onError: async err => {
            if (isAxiosError(err)) {
                if (err.code === 'ECONNABORTED') {
                    setIsOnline(false);
                    closeAllModals();
                }

                if (err.status === 401) {
                    const res = await refresh();

                    if (!res?.success) {
                        closeAllModals();
                    }
                }
            }
        },
        onSuccess: () => {
            setIsOnline(true);
        },
    }),
    mutationCache: new MutationCache({
        onError: async err => {
            if (isAxiosError(err)) {
                if (err.status === 401) {
                    const res = await refresh();

                    if (!res?.success) {
                        closeAllModals();
                    }
                    return;
                }

                if (err.code === 'ECONNABORTED') {
                    setIsOnline(false);
                    closeAllModals();
                } else {
                    setIsErrorOpen(true);
                }
            }
        },
        onSuccess: () => {
            setIsOnline(true);
        },
    }),
});

createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <BrowserRouter>
            <QueryClientProvider client={queryClient}>
                <App />
            </QueryClientProvider>
        </BrowserRouter>
    </StrictMode>
);
